// Displayed names for the BOE family. The ratio lives in the display string only —
// the product keys and labels stay un-suffixed so saved dashboards keep resolving.
// Shared with the GroupBy product options so the two lists cannot drift.
export const BOE_DISPLAY_NAMES: Record<string, string> = {
  BOE: "Wellhead BOE 6:1",
  "BOE 20": "Wellhead BOE 20:1",
  "BOE 30": "Wellhead BOE 30:1",
  "Sales BOE": "Sales BOE 6:1",
  "Sales BOE 20": "Sales BOE 20:1",
  "Sales BOE 30": "Sales BOE 30:1"
};

/**
 * True for a product in the BOE family. The family shares the "BOE" stem, which is a
 * substring of every longer family name and of the "(MBOE)" unit, so code that matches a
 * product by name has to hold this family to its full name. Read from the display-name
 * map — its keys are the product keys — so the family list cannot drift from the
 * displayed names.
 */
export const isBoeFamilyProduct = (product: string): boolean =>
  Object.hasOwn(BOE_DISPLAY_NAMES, product ?? "");

/**
 * Put the gas ratio back into a BOE label, and leave every other product alone. Takes a
 * label the caller has already resolved, because callers resolve it differently (the
 * selectors use getProductLabel with the entity kind, the legend uses
 * resolveProductLabel). One place for the lookup so the six BOE names cannot drift
 * between the selectors and the chart legend.
 */
export const getBoeRatioLabel = (label: string): string =>
  Object.hasOwn(BOE_DISPLAY_NAMES, label ?? "") ? BOE_DISPLAY_NAMES[label] : label;

/**
 * Put a trailing gas ratio on its own line. The BOE tiles in the product selectors are a
 * column of icon over label, so a name that fits on one line leaves its icon sitting
 * higher than the icons beside it — "Sales BOE 6:1" fits where "Sales BOE 20:1" wraps.
 * Breaking before the ratio makes every name in the family two lines, so the icons agree.
 *
 * The ratio is matched by shape rather than by value, so a ratio added later needs no
 * change here. Only the rendered string carries the break: the names in
 * BOE_DISPLAY_NAMES are read back as chart axis titles, and a newline in one of those
 * would stop an axis from finding its product.
 */
export const breakBeforeRatio = (label: string): string =>
  label ? label.replace(/ (\d+:1)$/, "\n$1") : label;

// To change chart title and axis labels go to ProductType.cs in eva-chart-svc
export function getShortenedProductName(
  product: string,
  rawLabel?: string,
  salesLabel?: string,
  inletRateLabel?: string,
  outletRateLabel?: string
): string {
  const productDict = {
    C2: "C₂",
    C3: "C₃",
    C4: "C₄",
    "C5+": "C₅₊",
    "Oil, Cond, C5+": "Oil, Cond, C₅₊",
    [`C₂ Yields ${rawLabel}`]: "C₂",
    [`C₃ Yields ${rawLabel}`]: "C₃",
    [`C₄ Yields ${rawLabel}`]: "C₄",
    [`C₅₊ Yields ${rawLabel}`]: "C₅₊",
    [`C₂ Yields ${salesLabel}`]: "C₂",
    [`C₃ Yields ${salesLabel}`]: "C₃",
    [`C₄ Yields ${salesLabel}`]: "C₄",
    [`C₅₊ Yields ${salesLabel}`]: "C₅₊",
    [`Liquids Yields ${salesLabel}`]: "Sales Liquids",
    [`Liquids Yields ${rawLabel}`]: "Sales Liquids",
    [`Oil, Cond, C₅₊ Yields ${rawLabel}`]: "Oil, Cond, C₅₊",
    [`Oil, Cond, C₅₊ Yields ${salesLabel}`]: "Oil, Cond, C₅₊",
    [`NGL Mix Yields ${salesLabel}`]: "NGL Mix",
    [`NGL Mix Yields ${rawLabel}`]: "NGL Mix",
    [`NGL₂₃₄ Yields ${salesLabel}`]: "NGL₂₃₄",
    [`NGL₂₃₄ Yields ${rawLabel}`]: "NGL₂₃₄",
    [`Sulphur Yields ${salesLabel}`]: "Sulphur",
    [`Sulphur Yields ${rawLabel}`]: "Sulphur",
    [`GP Water Yields ${salesLabel}`]: "GP Water",
    [`GP Water Yields ${rawLabel}`]: "GP Water",
    [`CO₂ Yields ${salesLabel}`]: "CO₂",
    [`CO₂ Yields ${rawLabel}`]: "CO₂",
    [`Fuel ${inletRateLabel}`]: "Fuel",
    [`Fuel ${outletRateLabel}`]: "Fuel",
    [`Flared ${inletRateLabel}`]: "Flared",
    [`Flared ${outletRateLabel}`]: "Flared",
    [`Vented ${inletRateLabel}`]: "Vented",
    [`Vented ${outletRateLabel}`]: "Vented",
    [`FFV ${inletRateLabel}`]: "FFV",
    [`FFV ${outletRateLabel}`]: "FFV",
    [`Liquids Shrinkage`]: "Liquids Shr.",
    [`Acid Gas Shrinkage`]: "AcGas Shr.",
    [`Acid Gas Disposition`]: "AcGas Disp.",
    [`Liquids Shrinkage ${inletRateLabel}`]: "Liquids Shr.",
    [`Liquids Shrinkage ${outletRateLabel}`]: "Liquids Shr.",
    [`Acid Gas Shrinkage ${inletRateLabel}`]: "AcGas Shr.",
    [`Acid Gas Shrinkage ${outletRateLabel}`]: "AcGas Shr.",
    [`Acid Gas Disposition ${inletRateLabel}`]: "AcGas Disp.",
    [`Acid Gas Disposition ${outletRateLabel}`]: "AcGas Disp.",
    [`Total Shrinkage Inlet`]: "Total Shr.",
    [`Total Shrinkage Outlet`]: "Total Shr.",
    ...BOE_DISPLAY_NAMES
  };

  if (Object.hasOwn(productDict, product ?? "")) {
    return productDict[product];
  } else {
    return product;
  }
}
